/**
 * Document-related utility functions
 */

/**
 * Returns an SVG icon component for different file types
 * @param fileType - The file extension/type (e.g., "PDF", "DOCX")
 * @returns JSX.Element - SVG icon component
 */
export function getDocumentIcon(fileType: string): React.JSX.Element {
  const getTextColor = (type: string): string => {
    switch (type.toUpperCase()) {
      case 'PDF':
        return '#DC2626'; // Red color
      case 'DOC':
      case 'DOCX':
        return '#2563EB'; // Blue color
      case 'XLS':
      case 'XLSX':
        return '#16A34A'; // Green color
      case 'PPT':
      case 'PPTX':
        return '#EA580C'; // Orange color
      case 'TXT':
      case 'CSV':
        return '#71717A'; // Gray color
      case 'JPG':
      case 'JPEG':
      case 'PNG':
      case 'GIF':
      case 'SVG':
      case 'WEBP':
        return '#9333EA'; // Purple color
      case 'ZIP':
      case 'RAR':
      case '7Z':
        return '#CA8A04'; // Yellow color
      case 'MP3':
      case 'MP4':
      case 'AVI':
      case 'MOV':
        return '#EC4899'; // Pink color
      default:
        return '#A1A1AA'; // Default gray
    }
  };

  return (
    <svg width="36" height="40" viewBox="0 0 36 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_262_2041)">
        <g filter="url(#filter0_d_262_2041)">
          <path
            d="M21.001 8.00098L24.5 11L27.9883 13.9893C27.9971 14.5928 28 15.2601 28 16V24C28 27.7712 27.9997 29.6566 26.8281 30.8281C25.6566 31.9997 23.7712 32 20 32H16C12.2288 32 10.3434 31.9997 9.17188 30.8281C8.0003 29.6566 8 27.7712 8 24V16C8 12.2288 8.0003 10.3434 9.17188 9.17188C10.3434 8.0003 12.2288 8 16 8H20C20.3495 8 20.6829 8.00004 21.001 8.00098Z"
            fill="url(#paint0_linear_262_2041)"
          />
          <mask
            id="mask0_262_2041"
            style={{ maskType: 'luminance' }}
            maskUnits="userSpaceOnUse"
            x="8"
            y="8"
            width="20"
            height="24"
          >
            <path
              d="M21.001 8.00098L24.5 11L27.9883 13.9893C27.9971 14.5928 28 15.2601 28 16V24C28 27.7712 27.9997 29.6566 26.8281 30.8281C25.6566 31.9997 23.7712 32 20 32H16C12.2288 32 10.3434 31.9997 9.17188 30.8281C8.0003 29.6566 8 27.7712 8 24V16C8 12.2288 8.0003 10.3434 9.17188 9.17188C10.3434 8.0003 12.2288 8 16 8H20C20.3495 8 20.6829 8.00004 21.001 8.00098Z"
              fill="white"
            />
          </mask>
          <g mask="url(#mask0_262_2041)">
            <path
              d="M21.001 8.00098L21.6518 7.24171L21.3722 7.00206L21.0039 7.00098L21.001 8.00098ZM27.9883 13.9893L28.9882 13.9746L28.9816 13.5235L28.639 13.2299L27.9883 13.9893ZM21.001 8.00098L20.3502 8.76025L23.8492 11.7593L24.5 11L25.1508 10.2407L21.6518 7.24171L21.001 8.00098ZM24.5 11L23.8492 11.7593L27.3376 14.7486L27.9883 13.9893L28.639 13.2299L25.1508 10.2407L24.5 11ZM27.9883 13.9893L26.9884 14.0039C26.9971 14.6004 27 15.2622 27 16H28H29C29 15.258 28.9971 14.5853 28.9882 13.9746L27.9883 13.9893ZM28 16H27V24H28H29V24H28ZM28 24H27C27 25.9138 26.9978 27.2486 26.8623 28.2556C26.7307 29.2339 26.4901 29.7519 26.121 30.121L26.8281 30.8281L27.5352 31.5352C28.3377 30.7327 28.683 29.7224 28.8445 28.5223C29.002 27.3509 29 25.8574 29 24H28ZM26.8281 30.8281L26.121 30.121C25.7519 30.4901 25.2339 30.7307 24.2556 30.8623C23.2486 30.9978 21.9138 31 20 31V32V33C21.8574 33 23.3509 33.002 24.5223 32.8445C25.7224 32.683 26.7327 32.3377 27.5352 31.5352L26.8281 30.8281ZM20 32V31H16V32V33H20V32ZM16 32V31C14.0862 31 12.7514 30.9978 11.7444 30.8623C10.7661 30.7307 10.2481 30.4901 9.87898 30.121L9.17188 30.8281L8.46477 31.5352C9.26727 32.3377 10.2776 32.683 11.4777 32.8445C12.6491 33.002 14.1426 33 16 33V32ZM9.17188 30.8281L9.87898 30.121C9.50991 29.7519 9.26927 29.2339 9.13767 28.2556C9.0022 27.2486 9 25.9138 9 24H8H7C7 25.8574 6.99795 27.3509 7.15553 28.5223C7.31697 29.7224 7.66227 30.7327 8.46477 31.5352L9.17188 30.8281ZM8 24H9V16H8H7V24H8ZM8 16H9C9 14.0862 9.0022 12.7514 9.13767 11.7444C9.26927 10.7661 9.50991 10.2481 9.87898 9.87898L9.17188 9.17188L8.46477 8.46477C7.66227 9.26727 7.31697 10.2776 7.15553 11.4777C6.99795 12.6491 7 14.1426 7 16H8ZM9.17188 9.17188L9.87898 9.87898C10.2481 9.50991 10.7661 9.26927 11.7444 9.13767C12.7514 9.0022 14.0862 9 16 9V8V7C14.1426 7 12.6491 6.99795 11.4777 7.15553C10.2776 7.31697 9.26727 7.66227 8.46477 8.46477L9.17188 9.17188ZM16 8V9H20V8V7H16V8ZM20 8V9C20.3497 9 20.6817 9.00004 20.998 9.00097L21.001 8.00098L21.0039 7.00098C20.6841 7.00004 20.3494 7 20 7V8Z"
              fill="black"
              fillOpacity="0.06"
            />
          </g>
        </g>
        <g filter="url(#filter1_f_262_2041)">
          <path d="M21 11V8L28 14H24C22.3431 14 21 12.6569 21 11Z" fill="black" fillOpacity="0.1" />
        </g>
        <path
          d="M21 11V8L28 14H24C22.3431 14 21 12.6569 21 11Z"
          fill="url(#paint1_linear_262_2041)"
        />
        <text
          x="18"
          y="25"
          textAnchor="middle"
          fill={getTextColor(fileType)}
          fontSize="8"
          fontFamily="Arial, sans-serif"
        >
          {fileType}
        </text>
      </g>
      <defs>
        <filter
          id="filter0_d_262_2041"
          x="0"
          y="0"
          width="36"
          height="40"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="4" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.04 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_262_2041" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_262_2041"
            result="shape"
          />
        </filter>
        <filter
          id="filter1_f_262_2041"
          x="17"
          y="4"
          width="15"
          height="14"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="2" result="effect1_foregroundBlur_262_2041" />
        </filter>
        <linearGradient
          id="paint0_linear_262_2041"
          x1="9.81818"
          y1="8"
          x2="28.2265"
          y2="30.3131"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#EFEEED" />
          <stop offset="1" stopColor="#EFEEED" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_262_2041"
          x1="28"
          y1="8"
          x2="22.0706"
          y2="14.9176"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#BFBEBD" />
          <stop offset="1" stopColor="#BFBEBD" />
        </linearGradient>
        <clipPath id="clip0_262_2041">
          <rect width="36" height="40" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}
